import { useEffect, useState } from 'react'
import { Download, Eye, EyeOff, UserPlus } from 'lucide-react'
import Button from '../../components/Button'
import { FormBody, FormCols, InfoNote } from '../../components/Form'
import FormRow from '../../components/FormRow'
import Drawer from '../../components/Drawer'
import TextField from '../../components/TextField'
import Select from '../../components/Select'
import { GROUP_TYPE } from '../../domain/domainCodes.js'
import { getUserInfoFromDataSource } from './usersService'
import { NAME_RULE } from './nameRule'
import { PASSWORD_RULE } from './passwordRule'
import { USERNAME_MAX_LENGTH, usernameProblem } from './usernameRule'
import { strings as S } from './strings'
import './modals.css'

const EMAIL_RULE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/* `groupId` is the department: chosen here, filled by a directory fetch, or left empty for
   the server's default department. The two names are typed (or fetched) — an account
   created without them showed its username in the name column until someone edited it. */
const EMPTY = {
  username: '',
  password: '',
  confirmPassword: '',
  email: '',
  arabicName: '',
  englishName: '',
  groupId: null,
}

/**
 * `#add_modal` in single-user mode — `showCreationForm(false)`.
 *
 * The legacy form also posts a hidden `workingPeriod` field whose row carries
 * `style="display:none"`, so it is not rendered here either.
 */
export default function UserCreateModal({ open, onClose, onSubmit, sources = [], groups = [], defaultSourceId }) {
  const [sourceId, setSourceId] = useState('')
  useEffect(() => {
    if (!open) return
    const configured = sources.find(source => String(source.id) === String(defaultSourceId))
    setSourceId(configured ? String(configured.id) : sources.length === 1 ? String(sources[0].id) : '')
  }, [open, defaultSourceId, sources])
  const [values, setValues] = useState(EMPTY)
  const [errors, setErrors] = useState({})
  const [reveal, setReveal] = useState({ password: false, confirmPassword: false })
  const [fetching, setFetching] = useState(false)
  const [fetched, setFetched] = useState(null)

  /* The list's "الإدارة" column shows a user's department, so only departments are offered —
     a custom group chosen here would leave that column empty. */
  const departments = groups.filter((group) => group.typeId === GROUP_TYPE.DEPARTMENTS)

  const set = (key) => (value) => setValues((previous) => ({ ...previous, [key]: value }))

  /**
   * Fill the form from the configured directory.
   *
   * Offered only when the payload says a fetch source exists. The reply names the
   * department rather than identifying it, so the group is matched by LABEL against the
   * page's own list — no match simply leaves the department unset, which the edit form can
   * fix, rather than sending a name where the handler parses an id.
   */
  const fetchFromSource = async () => {
    const username = values.username.trim()
    if (!username) {
      setErrors((previous) => ({ ...previous, username: S.fetch.usernameRequired }))
      return
    }

    if (!sourceId) {
      setErrors(previous => ({ ...previous, username: S.clone.sourcePlaceholder }))
      return
    }
    setFetching(true)
    const outcome = await getUserInfoFromDataSource(username, sourceId)
    setFetching(false)

    if (!outcome.ok || !outcome.profile) {
      setErrors((previous) => ({
        ...previous,
        username: outcome.message || S.fetch.notFound,
      }))
      setFetched(null)
      return
    }

    const profile = outcome.profile
    const group = groups.find(
      (option) => option.label === profile.groupNameAr || option.label === profile.groupNameEn,
    )

    setValues((previous) => ({
      ...previous,
      username: profile.userName || previous.username,
      email: profile.email || previous.email,
      arabicName: profile.nameAr,
      englishName: profile.nameEn,
      groupId: group?.value ?? null,
    }))
    setErrors((previous) => ({ ...previous, username: undefined, email: undefined }))
    setFetched({ ...profile, groupLabel: group?.label ?? profile.groupNameAr })
  }

  const validate = () => {
    const next = {}
    // The server's own four checks, in its order — usernameRule.js names the one that broke.
    const usernameError = usernameProblem(values.username)
    if (usernameError === 'usernameTooLong')
      next.username = S.create.errors.usernameTooLong(USERNAME_MAX_LENGTH)
    else if (usernameError) next.username = S.create.errors[usernameError]
    if (!values.password) next.password = S.create.errors.passwordRequired
    else if (!PASSWORD_RULE.test(values.password)) next.password = S.create.errors.passwordFormat
    if (!values.confirmPassword) next.confirmPassword = S.create.errors.confirmRequired
    else if (values.confirmPassword !== values.password)
      next.confirmPassword = S.create.errors.confirmMismatch
    if (!values.email.trim()) next.email = S.create.errors.emailRequired
    else if (!EMAIL_RULE.test(values.email)) next.email = S.create.errors.emailFormat
    if (!values.arabicName?.trim()) next.arabicName = S.create.errors.arabicNameRequired
    else if (!NAME_RULE.test(values.arabicName)) next.arabicName = S.nameNeedsLetters
    if (!values.englishName?.trim()) next.englishName = S.create.errors.englishNameRequired
    else if (!NAME_RULE.test(values.englishName)) next.englishName = S.nameNeedsLetters
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const submit = async (event) => {
    event.preventDefault()
    if (!validate()) return
    // `confirmPassword` is not part of the legacy request body.
    const { confirmPassword: _confirmPassword, ...body } = values

    /* CLEAR ONLY ON SUCCESS. This used to reset the form on the line after `onSubmit`,
       synchronously — so a refusal the operator could act on ("that username is taken",
       "that password misses the policy") arrived over an empty form, and everything they
       had typed, both passwords included, had to be entered again. `onSubmit` resolves to
       whether the write succeeded. */
    const saved = await onSubmit(body)
    if (saved === false) return

    setValues(EMPTY)
    setErrors({})
    setFetched(null)
  }

  /* The policy is checked on submit and its message names every rule; the live
     checklist under the field was taken out at the client's request (2026-10-22). */
  const passwordField = (key, label) => (
    <FormRow label={label} error={errors[key]} required>
      {(id, describedBy) => (
        <TextField
          id={id}
          aria-describedby={describedBy}
          type={reveal[key] ? 'text' : 'password'}
          value={values[key]}
          onChange={set(key)}
          invalid={!!errors[key]}
          autoComplete="new-password"
          trailing={
            <button
              type="button"
              onClick={() => setReveal((previous) => ({ ...previous, [key]: !previous[key] }))}
              aria-label={label}
            >
              {reveal[key] ? <EyeOff size={17} /> : <Eye size={17} />}
            </button>
          }
        />
      )}
    </FormRow>
  )

  return (
    <Drawer
      dialog
      open={open}
      title={S.create.title}
      icon={UserPlus}
      hint={S.create.hint}
      onClose={onClose}
      footer={
        <>
          <Button type="submit" form="create-user-form">
            {S.create.submit}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {S.close}
          </Button>
        </>
      }
    >
      <form id="create-user-form" onSubmit={submit} noValidate>
        <FormBody>
          {sources.length > 1 && (
            <FormRow label={S.clone.source}>
              {id => <Select id={id} value={sourceId} onChange={setSourceId}
                options={sources.map(source => ({ value: String(source.id), label: source.name }))}
                placeholder={S.clone.sourcePlaceholder} />}
            </FormRow>
          )}
          {/* The fetch sits ON the username row because the username is what it takes —
              a button anywhere else would be a second thing to find. */}
          <FormRow
            label={S.create.username}
            error={errors.username}
            hint={sources.length > 0 ? S.fetch.hint : undefined}
            required
          >
            {(id) => (
              <div className="user-create__username">
                <TextField
                  id={id}
                  value={values.username}
                  onChange={set('username')}
                  invalid={!!errors.username}
                  autoComplete="off"
                />
                {sources.length > 0 && (
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={fetching}
                    onClick={fetchFromSource}
                  >
                    <Download size={16} strokeWidth={1.75} />
                    {fetching ? S.fetch.running : S.fetch.action}
                  </Button>
                )}
              </div>
            )}
          </FormRow>

          {/* What was fetched, named — the fields below were filled from it, and a department
              the page does not know is left empty rather than guessed. */}
          {fetched && (
            <InfoNote lead={S.fetch.filled}>
              <bdi>
                {[fetched.nameAr || fetched.nameEn, fetched.groupLabel]
                  .filter(Boolean)
                  .join(' — ')}
              </bdi>
            </InfoNote>
          )}

          <FormRow label={S.create.email} error={errors.email} required>
            {(id) => (
              <TextField
                id={id}
                type="email"
                dir="ltr"
                value={values.email}
                onChange={set('email')}
                invalid={!!errors.email}
                autoComplete="off"
              />
            )}
          </FormRow>

          <FormCols>
            <FormRow label={S.edit.nameAr} error={errors.arabicName} required>
              {(id) => (
                <TextField
                  id={id}
                  value={values.arabicName ?? ''}
                  onChange={set('arabicName')}
                  invalid={!!errors.arabicName}
                  autoComplete="off"
                />
              )}
            </FormRow>
            <FormRow label={S.edit.nameEn} error={errors.englishName} required>
              {(id) => (
                <TextField
                  id={id}
                  dir="ltr"
                  value={values.englishName ?? ''}
                  onChange={set('englishName')}
                  invalid={!!errors.englishName}
                  autoComplete="off"
                />
              )}
            </FormRow>
          </FormCols>

          {/* Optional: left empty, the server files the account under the default department. */}
          <FormRow label={S.edit.group}>
            {(id) => (
              <Select
                id={id}
                value={values.groupId == null ? '' : String(values.groupId)}
                onChange={(groupId) => set('groupId')(groupId === '' ? null : groupId)}
                options={departments.map((group) => ({ value: String(group.value), label: group.label }))}
                placeholder={S.create.defaultDepartment}
              />
            )}
          </FormRow>

          {/* The password and its confirmation are ONE decision, so they sit on
              one line where the two can be compared without scrolling. */}
          <FormCols>
            {passwordField('password', S.create.password)}
            {passwordField('confirmPassword', S.create.confirmPassword)}
          </FormCols>
        </FormBody>
      </form>
    </Drawer>
  )
}
